import {
  Building2,
  ChevronDown,
  ChevronRight,
  Clock,
  MapPin,
  Wrench,
} from "lucide-react";
import { PlanningCalendar } from "../PlanningCalendar";
import type { Job, StockRegion, User } from "../../types";

export interface AdminPlanningPanelProps {
  jobs: Job[];
  installers: User[];
  partners: User[];
  stockRegions: StockRegion[];
  planningRegionId: string;
  planningPartnerId: string;
  planningInstallerId: string;
  planningInProgressJobs: Job[];
  onPlanningRegionIdChange: (id: string) => void;
  onPlanningPartnerIdChange: (id: string) => void;
  onPlanningInstallerIdChange: (id: string) => void;
  onJobSelect: (job: Job) => void;
  onUpdateJob?: (job: Job) => void;
  onAddJobAtDate: (date: string) => void;
  labels: {
    filterPlanning: string;
    regionLabel: string;
    partnerLabel: string;
    installerLabel: string;
    allRegions: string;
    allPartners: string;
    allInstallers: string;
    jobsInProgress: string;
    noJobsInProgress: string;
  };
}

export function AdminPlanningPanel({
  jobs,
  installers,
  partners,
  stockRegions,
  planningRegionId,
  planningPartnerId,
  planningInstallerId,
  planningInProgressJobs,
  onPlanningRegionIdChange,
  onPlanningPartnerIdChange,
  onPlanningInstallerIdChange,
  onJobSelect,
  onUpdateJob,
  onAddJobAtDate,
  labels,
}: AdminPlanningPanelProps) {
  const calendarJobs = jobs.filter((j) => {
    if (!j.date) return false;
    if (planningRegionId && j.stockRegionId !== planningRegionId) return false;
    if (planningPartnerId && j.partnerId !== planningPartnerId) return false;
    if (planningInstallerId && j.installerId !== planningInstallerId) return false;
    return true;
  });

  return (
    <div className="space-y-4 min-w-0">
      <div className="rounded-[28px] overflow-hidden bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border border-gray-200/60 dark:border-white/10 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.02)] dark:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.3)]">
        <div className="p-5 md:p-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400 dark:text-slate-500 mb-4">
            {labels.filterPlanning}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="relative">
              <label className="block text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">
                {labels.regionLabel}
              </label>
              <div className="relative">
                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
                <select
                  value={planningRegionId}
                  onChange={(e) => onPlanningRegionIdChange(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 rounded-2xl border border-gray-200/80 dark:border-white/10 bg-gray-50/80 dark:bg-white/5 text-gray-900 dark:text-white text-[15px] font-medium focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400/50 outline-none transition-all appearance-none cursor-pointer"
                >
                  <option value="">{labels.allRegions}</option>
                  {stockRegions.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
              </div>
            </div>
            <div className="relative">
              <label className="block text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">
                {labels.partnerLabel}
              </label>
              <div className="relative">
                <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
                <select
                  value={planningPartnerId}
                  onChange={(e) => onPlanningPartnerIdChange(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 rounded-2xl border border-gray-200/80 dark:border-white/10 bg-gray-50/80 dark:bg-white/5 text-gray-900 dark:text-white text-[15px] font-medium focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400/50 outline-none transition-all appearance-none cursor-pointer"
                >
                  <option value="">{labels.allPartners}</option>
                  {partners.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name ?? p.company ?? p.id}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
              </div>
            </div>
            <div className="relative">
              <label className="block text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">
                {labels.installerLabel}
              </label>
              <div className="relative">
                <Wrench className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
                <select
                  value={planningInstallerId}
                  onChange={(e) => onPlanningInstallerIdChange(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 rounded-2xl border border-gray-200/80 dark:border-white/10 bg-gray-50/80 dark:bg-white/5 text-gray-900 dark:text-white text-[15px] font-medium focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400/50 outline-none transition-all appearance-none cursor-pointer"
                >
                  <option value="">{labels.allInstallers}</option>
                  {installers.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto min-w-0 -mx-1 px-1 sm:mx-0 sm:px-0">
        <PlanningCalendar
          jobs={calendarJobs}
          installers={installers}
          partners={partners}
          onJobSelect={onJobSelect}
          onUpdateJob={onUpdateJob}
          onAddJob={onAddJobAtDate}
        />
      </div>

      <div className="rounded-2xl overflow-hidden bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl border border-gray-200/60 dark:border-white/10 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.02)] dark:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.3)]">
        <div className="p-4 md:p-5 border-b border-gray-100 dark:border-white/5">
          <h3 className="text-[15px] font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Clock size={18} className="text-amber-500" />
            {labels.jobsInProgress}
          </h3>
        </div>
        <div className="p-4 md:p-5">
          {planningInProgressJobs.length === 0 ? (
            <p className="text-[14px] text-gray-500 dark:text-slate-400">{labels.noJobsInProgress}</p>
          ) : (
            <ul className="space-y-2">
              {planningInProgressJobs.map((job) => {
                const installerName = job.installerId
                  ? installers.find((i) => i.id === job.installerId)?.name
                  : null;
                const partnerUser = job.partnerId
                  ? partners.find((p) => p.id === job.partnerId)
                  : undefined;
                const partnerName = partnerUser?.company ?? partnerUser?.name ?? null;
                return (
                  <li key={job.id}>
                    <button
                      type="button"
                      onClick={() => onJobSelect(job)}
                      className="w-full text-left flex flex-wrap items-center gap-x-3 gap-y-1 p-3 rounded-xl border border-amber-200/60 dark:border-amber-500/20 bg-amber-50/80 dark:bg-amber-500/10 hover:bg-amber-100/80 dark:hover:bg-amber-500/20 transition-colors"
                    >
                      <span className="font-medium text-gray-900 dark:text-white truncate">{job.clientName}</span>
                      {job.date && (
                        <span className="text-[13px] text-gray-500 dark:text-slate-400">
                          {new Date(job.date + "T12:00:00").toLocaleDateString("fr-FR", {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                          })}
                          {job.time ? ` · ${job.time}` : ""}
                        </span>
                      )}
                      {partnerName && (
                        <span className="text-[13px] text-gray-500 dark:text-slate-400 shrink-0">{partnerName}</span>
                      )}
                      {installerName && (
                        <span className="text-[13px] text-gray-500 dark:text-slate-400">{installerName}</span>
                      )}
                      <ChevronRight size={16} className="ml-auto text-gray-400 dark:text-slate-500 shrink-0" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
